import { useState, useEffect } from "react";

const s = {
  container: { padding: "20px", maxWidth: "600px", margin: "0 auto" },
  section: {
    background: "#1e293b", border: "1px solid #334155",
    borderRadius: "10px", padding: "24px", marginBottom: "20px",
  },
  sectionTitle: {
    fontSize: "14px", fontWeight: 600, color: "#94a3b8",
    textTransform: "uppercase", letterSpacing: "0.06em",
    marginBottom: "20px", paddingBottom: "10px",
    borderBottom: "1px solid #334155",
  },
  row: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: "16px", marginBottom: "16px",
  },
  label: { fontSize: "14px", color: "#e2e8f0", flex: 1 },
  sublabel: { fontSize: "12px", color: "#64748b", marginTop: "2px" },
  toggle: {
    position: "relative", display: "inline-block", width: 44, height: 24,
    flexShrink: 0,
  },
  toggleInput: { opacity: 0, width: 0, height: 0, position: "absolute" },
  numberInput: {
    background: "#0f172a", border: "1px solid #334155", borderRadius: "6px",
    color: "#e2e8f0", padding: "6px 10px", fontSize: "14px",
    fontFamily: "inherit", width: 80, textAlign: "right",
  },
  saveBtn: {
    background: "#3b82f6", color: "white", border: "none",
    borderRadius: "6px", padding: "10px 24px", fontSize: "14px",
    fontWeight: 600, cursor: "pointer", marginTop: "8px",
  },
  saveBtnDisabled: {
    background: "#1e3a5f", color: "#64748b", border: "none",
    borderRadius: "6px", padding: "10px 24px", fontSize: "14px",
    fontWeight: 600, cursor: "not-allowed", marginTop: "8px",
  },
  status: { fontSize: "13px", marginTop: "12px", minHeight: 20 },
  statusOk: { color: "#22c55e" },
  statusErr: { color: "#ef4444" },
  loading: { color: "#64748b", padding: "24px", textAlign: "center" },
};

function Toggle({ checked, onChange }) {
  const bg = checked ? "#3b82f6" : "#334155";
  return (
    <label style={s.toggle}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={s.toggleInput}
      />
      <span
        style={{
          position: "absolute", inset: 0, borderRadius: 24,
          background: bg, transition: "background 0.2s", cursor: "pointer",
        }}
      />
      <span
        style={{
          position: "absolute", top: 3, left: checked ? 23 : 3,
          width: 18, height: 18, borderRadius: "50%", background: "white",
          transition: "left 0.2s", cursor: "pointer",
        }}
      />
    </label>
  );
}

function ServerSettings({ savedHost }) {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [status, setStatus]   = useState(null); // { ok: bool, msg: string }

  useEffect(() => {
    fetch(`${savedHost}/api/settings`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => { setSettings(data); setDraft(data); })
      .catch(() => setStatus({ ok: false, msg: "Einstellungen konnten nicht geladen werden." }));
  }, [savedHost]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetch(`${savedHost}/api/settings`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const data = await res.json();
      if (!res.ok) {
        setStatus({ ok: false, msg: data.error || "Fehler beim Speichern." });
      } else {
        setSettings(data);
        setDraft(data);
        setStatus({ ok: true, msg: "Gespeichert." });
      }
    } catch {
      setStatus({ ok: false, msg: "Verbindungsfehler." });
    } finally {
      setSaving(false);
    }
  };

  if (!draft) {
    return <div style={s.loading}>{status?.msg ?? "Lade Einstellungen…"}</div>;
  }

  return (
    <div style={s.container}>
      <div style={s.section}>
        <div style={s.sectionTitle}>Rate-Limiter</div>

        <div style={s.row}>
          <div>
            <div style={s.label}>Rate Limit aktivieren</div>
            <div style={s.sublabel}>Begrenzt Uploads pro Session</div>
          </div>
          <Toggle
            checked={draft.rateLimiter.enabled}
            onChange={(val) =>
              setDraft((d) => ({ ...d, rateLimiter: { ...d.rateLimiter, enabled: val } }))
            }
          />
        </div>

        <div style={s.row}>
          <div>
            <div style={s.label}>Rate-Limit Zeitfenster</div>
            <div style={s.sublabel}>Minuten bis der Zähler zurückgesetzt wird (1–120)</div>
          </div>
          <input
            type="number"
            min={1}
            max={120}
            value={draft.rateLimiter.windowMinutes}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                rateLimiter: { ...d.rateLimiter, windowMinutes: Number(e.target.value) },
              }))
            }
            style={{ ...s.numberInput, opacity: draft.rateLimiter.enabled ? 1 : 0.4 }}
            disabled={!draft.rateLimiter.enabled}
          />
        </div>

        <div style={s.row}>
          <div>
            <div style={s.label}>Max. Uploads pro Zeitfenster</div>
            <div style={s.sublabel}>Anzahl erlaubter Uploads pro Session (1–100)</div>
          </div>
          <input
            type="number"
            min={1}
            max={100}
            value={draft.rateLimiter.maxRequests}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                rateLimiter: { ...d.rateLimiter, maxRequests: Number(e.target.value) },
              }))
            }
            style={{ ...s.numberInput, opacity: draft.rateLimiter.enabled ? 1 : 0.4 }}
            disabled={!draft.rateLimiter.enabled}
          />
        </div>

        <button
          style={isDirty && !saving ? s.saveBtn : s.saveBtnDisabled}
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          {saving ? "Speichern…" : "Speichern"}
        </button>

        {status && (
          <div style={{ ...s.status, ...(status.ok ? s.statusOk : s.statusErr) }}>
            {status.msg}
          </div>
        )}
      </div>
    </div>
  );
}

export default ServerSettings;
